import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ProductService } from './product.service';
import { Product } from '../models/product.model';

export interface AuthUser {
  role: string;
  name: string;
  email: string;
  tenant_company_id: string;
  active_product?: { id: number; slug: string } | null;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
  products?: Product[];
}

export interface RefreshResponse {
  access_token: string;
}

interface JwtPayload {
  exp: number;
  iat: number;
  sub: string;
  [key: string]: unknown;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'sv_access_token',
  REFRESH_TOKEN: 'sv_refresh_token',
  USER: 'sv_user',
} as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly productService = inject(ProductService);
  private readonly apiUrl = environment.apiUrl;

  private readonly userSubject = new BehaviorSubject<AuthUser | null>(this.getStoredUser());
  readonly user$ = this.userSubject.asObservable();

  private isRefreshing = false;

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/admin/auth/login`, { email, password })
      .pipe(
        tap((response) => {
          this.storeTokens(response.access_token, response.refresh_token);
          this.storeUser(response.user);
          this.userSubject.next(response.user);
          this.applyProductContext(response.products, response.user.active_product);
        }),
      );
  }

  refresh(): Observable<RefreshResponse> {
    const refreshToken = this.getRefreshToken();

    return this.http
      .post<RefreshResponse>(`${this.apiUrl}/admin/auth/refresh`, {
        refresh_token: refreshToken,
      })
      .pipe(
        tap((response) => {
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.access_token);
        }),
        catchError((error) => {
          this.clearStorage();
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
      );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/admin/auth/logout`, {}).pipe(
      tap(() => {
        this.clearStorage();
        this.userSubject.next(null);
        this.productService.clear();
        this.router.navigate(['/login']);
      }),
      catchError((error) => {
        this.clearStorage();
        this.userSubject.next(null);
        this.productService.clear();
        this.router.navigate(['/login']);
        return throwError(() => error);
      }),
    );
  }

  getMe(): Observable<{ user: AuthUser; products?: Product[] }> {
    return this.http.get<{ user: AuthUser; products?: Product[] }>(`${this.apiUrl}/admin/auth/me`).pipe(
      tap((response) => {
        this.storeUser(response.user);
        this.userSubject.next(response.user);
        this.applyProductContext(response.products, response.user.active_product);
      }),
    );
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      const payload = this.decodeJwt(token);
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  getUser(): AuthUser | null {
    return this.userSubject.value;
  }

  get isRefreshingToken(): boolean {
    return this.isRefreshing;
  }

  set isRefreshingToken(value: boolean) {
    this.isRefreshing = value;
  }

  private applyProductContext(
    products?: Product[],
    activeProduct?: { id: number; slug: string } | null,
  ): void {
    if (products) {
      this.productService.setProducts(products);
    }
    if (activeProduct) {
      const full = products?.find((p) => p.id === activeProduct.id);
      this.productService.setActiveProduct(
        full ?? { id: activeProduct.id, slug: activeProduct.slug, name: activeProduct.slug },
      );
    }
  }

  private storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  private storeUser(user: AuthUser): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  private getStoredUser(): AuthUser | null {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    if (!userStr) {
      return null;
    }
    try {
      return JSON.parse(userStr) as AuthUser;
    } catch {
      return null;
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  private decodeJwt(token: string): JwtPayload {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT token');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JwtPayload;
  }
}
